import React from 'react';
import { Sun, Moon, Monitor, Type, Eye } from 'lucide-react';

const SettingsPanel = ({ settings, onSettingChange, announce }) => {
  const handleThemeChange = (theme) => {
    onSettingChange('theme', theme);
    announce(`Theme changed to ${theme}`);
  };

  const handleFontSizeChange = (size) => {
    onSettingChange('fontSize', size);
    announce(`Font size changed to ${size}`);
  };

  const handleFontFamilyChange = (family) => {
    onSettingChange('fontFamily', family);
    announce(`Font family changed to ${family}`);
  };

  const handleHighContrastToggle = () => {
    const newValue = !settings.highContrast;
    onSettingChange('highContrast', newValue);
    announce(`High contrast mode ${newValue ? 'enabled' : 'disabled'}`);
  };

  const handleReducedMotionToggle = () => {
    const newValue = !settings.reducedMotion;
    onSettingChange('reducedMotion', newValue);
    announce(`Reduced motion ${newValue ? 'enabled' : 'disabled'}`);
  };

  const handleScreenReaderToggle = () => {
    const newValue = !settings.screenReaderOptimized;
    onSettingChange('screenReaderOptimized', newValue);
    announce(`Screen reader optimization ${newValue ? 'enabled' : 'disabled'}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-900 mt-2">Customize your accessibility preferences</p>
      </div>

      {/* Theme Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Sun size={20} className="mr-2" />
          Theme
        </h2>
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            {[
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'auto', label: 'Auto', icon: Monitor }
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => handleThemeChange(value)}
                className={`flex items-center px-4 py-2 rounded-md border transition-colors ${
                  settings.theme === value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Icon size={16} className="mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Font Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Type size={20} className="mr-2" />
          Typography
        </h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Font Size</label>
            <div className="flex items-center space-x-2">
              {[
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
                { value: 'xl', label: 'Extra Large' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleFontSizeChange(value)}
                  className={`px-3 py-1 rounded border transition-colors ${
                    settings.fontSize === value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Font Family</label>
            <div className="flex items-center space-x-2">
              {[
                { value: 'default', label: 'Default' },
                { value: 'dyslexic', label: 'Dyslexic-Friendly' },
                { value: 'serif', label: 'Serif' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleFontFamilyChange(value)}
                  className={`px-3 py-1 rounded border transition-colors ${
                    settings.fontFamily === value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Accessibility Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Eye size={20} className="mr-2" />
          Accessibility
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">High Contrast Mode</h3>
              <p className="text-sm text-gray-600">Increase contrast for better visibility</p>
            </div>
            <button
              onClick={handleHighContrastToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                settings.highContrast ? 'bg-primary' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.highContrast}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                  settings.highContrast ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Reduce Motion</h3>
              <p className="text-sm text-gray-600">Minimize animations and transitions</p>
            </div>
            <button
              onClick={handleReducedMotionToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                settings.reducedMotion ? 'bg-primary' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.reducedMotion}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                  settings.reducedMotion ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Screen Reader Optimization</h3>
              <p className="text-sm text-gray-600">Enhance compatibility with screen readers</p>
            </div>
            <button
              onClick={handleScreenReaderToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                settings.screenReaderOptimized ? 'bg-primary' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={settings.screenReaderOptimized}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                  settings.screenReaderOptimized ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;